import { AlertTriangle } from 'lucide-react';
import type { UnitCountReconciliation, UnitMixAnalysis } from '../../types/calculations';

interface UnitValidationSectionProps {
  totalUnits: number;
  unitSource?: string;
  unitConfidence?: string;
  unitCountReconciliation?: UnitCountReconciliation;
  /** Phase 212: Unit mix with bed/bath breakdown */
  unitMixAnalysis?: UnitMixAnalysis;
}

function confidenceDot(confidence?: string) {
  const c = (confidence || '').toUpperCase();
  if (c === 'HIGH') return 'bg-emerald-400';
  if (c === 'MEDIUM') return 'bg-amber-400';
  if (c === 'LOW' || c === 'FALLBACK') return 'bg-red-400';
  return 'bg-stone-500';
}

/** Parse bed count from unit type string like "1BR", "2BR", "Studio" */
function parseBedCount(unitType: string): string {
  const t = unitType.toUpperCase();
  if (t.includes('STUDIO') || t === '0BR') return 'Studio';
  const match = t.match(/^(\d+)BR/);
  if (match) return `${match[1]}bd`;
  return unitType;
}

export function UnitValidationSection({
  totalUnits,
  unitSource,
  unitConfidence,
  unitCountReconciliation,
  unitMixAnalysis,
}: UnitValidationSectionProps) {
  const hasMixData = unitMixAnalysis && Object.keys(unitMixAnalysis.unit_mix || {}).length > 0;
  const hasEstimatedRents = unitMixAnalysis && (unitMixAnalysis.estimated_rents || []).length > 0;

  return (
    <div>
      {/* Gold section header */}
      <div className="flex items-center gap-3 pt-2">
        <h2 className="text-sm font-semibold tracking-widest text-gold uppercase">
          Unit Validation
        </h2>
        <div className="flex-1 border-t border-gold/30" />
      </div>

      {unitCountReconciliation ? (
        <div className="mt-3 bg-charcoal/40 border border-slate-dark rounded-lg p-4 space-y-3">
          {/* Conflict detection banner */}
          {unitCountReconciliation.conflict_detected && (
            <div className="flex items-center gap-2 bg-amber-900/20 border border-amber-600/30 rounded px-3 py-2">
              <AlertTriangle size={14} className="text-amber-400 shrink-0" />
              <span className="text-sm font-medium text-amber-400">
                Source Conflict Detected
              </span>
            </div>
          )}

          {/* Multi-source table */}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-sm uppercase tracking-wider text-stone-400 border-b border-slate-dark">
                <th className="text-left py-1.5 font-medium">Source</th>
                <th className="text-center py-1.5 font-medium">Units</th>
                <th className="text-center py-1.5 font-medium">Confidence</th>
                <th className="text-left py-1.5 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {unitCountReconciliation.sources_checked.map((src) => (
                <tr key={src.source_name} className="border-b border-slate-dark/50">
                  <td className="py-1.5 text-ivory text-sm">
                    {src.source_name}
                    {src.source_name === unitCountReconciliation.resolved_source && (
                      <span className="ml-1.5 text-sm bg-gold/20 text-gold px-1.5 py-0.5 rounded">
                        SELECTED
                      </span>
                    )}
                  </td>
                  <td className="text-center py-1.5 font-mono text-ivory text-sm">
                    {src.unit_count}
                  </td>
                  <td className="text-center py-1.5">
                    <div className="flex items-center justify-center gap-1">
                      <div className={`w-1.5 h-1.5 rounded-full ${confidenceDot(src.confidence)}`} />
                      <span className="text-sm text-stone-400 uppercase">{src.confidence}</span>
                    </div>
                  </td>
                  <td className="py-1.5 text-sm text-stone-400">
                    {src.notes || '\u2014'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Conflict details */}
          {unitCountReconciliation.conflict_details && (
            <div className="bg-amber-900/10 border border-amber-600/20 rounded px-3 py-2">
              <p className="text-sm text-amber-400">
                {unitCountReconciliation.conflict_details}
              </p>
            </div>
          )}

          {/* Resolution summary */}
          <div className="text-sm text-stone-400">
            Resolved:{' '}
            <span className="text-ivory font-mono">
              {unitCountReconciliation.resolved_count}
            </span>{' '}
            units from{' '}
            <span className="text-ivory">
              {unitCountReconciliation.resolved_source}
            </span>{' '}
            (
            <span className="text-stone-400">
              {unitCountReconciliation.resolved_confidence} confidence
            </span>
            )
          </div>
        </div>
      ) : (
        /* Compact fallback when no reconciliation data */
        <div className="mt-3 bg-charcoal/40 border border-slate-dark rounded-lg px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-ivory font-mono font-bold text-base">
              {totalUnits}
            </span>
            <span className="text-stone-400 text-sm">
              units from {unitSource || 'Unknown'}
            </span>
            <div className="flex items-center gap-1">
              <div className={`w-1.5 h-1.5 rounded-full ${confidenceDot(unitConfidence)}`} />
              <span className="text-sm text-stone-400 uppercase">
                {unitConfidence || 'Unknown'}
              </span>
            </div>
          </div>
          <p className="text-sm text-stone-400 mt-1">
            Single-source unit count (no reconciliation data available)
          </p>
        </div>
      )}

      {/* Phase 212: Unit Mix Breakdown — bed/bath per unit */}
      {hasMixData ? (
        <div className="mt-3 bg-charcoal/40 border border-slate-dark rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-xs font-semibold tracking-widest text-stone-400 uppercase">
              Unit Mix Breakdown
            </h3>
            <div className="flex-1 border-t border-slate-dark" />
          </div>
          {hasEstimatedRents ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-stone-400 border-b border-slate-dark">
                  <th className="text-left py-1.5 font-medium">Unit Type</th>
                  <th className="text-center py-1.5 font-medium">Beds</th>
                  <th className="text-right py-1.5 font-medium">Sqft</th>
                  <th className="text-right py-1.5 font-medium">Est. Rent</th>
                </tr>
              </thead>
              <tbody>
                {unitMixAnalysis!.estimated_rents.map((unit, idx) => (
                  <tr key={idx} className="border-b border-slate-dark/50">
                    <td className="py-1.5 text-ivory text-sm">
                      {unit.unit_type}
                    </td>
                    <td className="text-center py-1.5 text-stone-400 text-sm">
                      {parseBedCount(unit.unit_type)}
                    </td>
                    <td className="text-right py-1.5 font-mono text-stone-400 text-sm">
                      {unit.sqft ? `${unit.sqft.toLocaleString()} sqft` : '\u2014'}
                    </td>
                    <td className="text-right py-1.5 font-mono text-ivory text-sm">
                      ${unit.monthly_rent.toLocaleString()}/mo
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            /* Mix without per-unit rent estimates */
            <div className="space-y-1.5">
              {Object.entries(unitMixAnalysis!.unit_mix).map(([unitType, count]) => (
                <div key={unitType} className="flex items-center justify-between text-sm">
                  <span className="text-ivory">{unitType}</span>
                  <span className="text-stone-400">{count} unit{count !== 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-2 flex items-center gap-1.5">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-900/30 text-amber-400 font-medium">
              Estimated
            </span>
            <span className="text-xs text-stone-400">
              Inferred from sqft/unit &mdash; verify with OPA or L&I records
            </span>
          </div>
        </div>
      ) : unitMixAnalysis ? (
        <div className="mt-3 bg-charcoal/40 border border-slate-dark rounded-lg px-4 py-3">
          <p className="text-sm text-stone-400">
            Unit mix data unavailable &mdash; L&I/OPA data pending
          </p>
        </div>
      ) : null}
    </div>
  );
}
